import { CheckIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

interface AuthLayoutProps {
	children: ReactNode;
}

// Same texture recipe as the AboutUs hero (src/app/Views/StaticPages/AboutUs/AboutUs.tsx)
const dotGridStyle: CSSProperties = {
	backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
	backgroundSize: "32px 32px",
};

const noiseStyle: CSSProperties = {
	backgroundImage:
		"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
};

const MOCK_JOBS = [
	{
		initials: "VT",
		title: "Sales Manager",
		detail: "Vodacom · Dar es Salaam",
		salary: "TZS 2.4M",
	},
	{
		initials: "KF",
		title: "Accountant",
		detail: "Kilimo Fresh · Arusha",
		salary: "TZS 1.6M",
	},
	{
		initials: "MB",
		title: "Civil Engineer",
		detail: "Mwanza Builders · Mwanza",
		salary: "TZS 2.1M",
	},
];

const MATCH_RING_CIRCUMFERENCE = 2 * Math.PI * 11;

// Deterministic so every render places the particles identically.
function seededParticles(count: number) {
	let seed = 42;
	const random = () => {
		seed = (seed * 1664525 + 1013904223) % 4294967296;
		return seed / 4294967296;
	};
	return Array.from({ length: count }, (_, id) => ({
		id,
		left: random() * 100,
		top: random() * 100,
		size: 2 + random() * 2.5,
		opacity: 0.15 + random() * 0.4,
		duration: 7 + random() * 8,
		delay: -random() * 15,
		driftX: 4 + random() * 8,
		driftY: 8 + random() * 14,
	}));
}

const PARTICLES = seededParticles(26);

function AuthLayout({ children }: AuthLayoutProps) {
	return (
		<section className="grid min-h-screen grid-cols-1 md:h-screen md:grid-cols-2">
			<div className="relative hidden flex-col overflow-hidden bg-blue-800 p-10 lg:p-14 md:flex">
				<div aria-hidden="true" className="pointer-events-none absolute inset-0">
					<div className="absolute inset-0 opacity-[0.04]" style={dotGridStyle} />
					<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.25)_0%,transparent_60%)]" />
					<div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-blue-950/60 blur-3xl" />
					{PARTICLES.map((particle) => (
						<span
							className="auth-particle absolute rounded-full bg-white"
							key={particle.id}
							style={
								{
									left: `${particle.left}%`,
									top: `${particle.top}%`,
									width: `${particle.size}px`,
									height: `${particle.size}px`,
									opacity: particle.opacity,
									"--particle-duration": `${particle.duration}s`,
									"--particle-delay": `${particle.delay}s`,
									"--particle-drift-x": `${particle.driftX}px`,
									"--particle-drift-y": `-${particle.driftY}px`,
								} as CSSProperties
							}
						/>
					))}
					<div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={noiseStyle} />
				</div>

				<Link to="/" className="relative block w-fit">
					<img src="/media-kit/logos/logo-dark.svg" alt="DProz Logo" className="w-[140px]" />
				</Link>

				<div className="relative flex flex-1 flex-col items-center justify-center">
					<div aria-hidden="true" className="relative w-80 py-8">
						<div className="absolute inset-x-0 top-8 bottom-8 translate-x-4 translate-y-3 rotate-2 rounded-2xl bg-white/15" />
						<div className="relative rounded-2xl bg-white p-5 shadow-2xl shadow-primary-950/40">
							<div className="flex items-center justify-between">
								<p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">New matches</p>
								<span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-bold text-primary-700">
									3 today
								</span>
							</div>
							<div className="mt-4 flex flex-col gap-4">
								{MOCK_JOBS.map((job) => (
									<div className="flex items-center gap-3" key={job.title}>
										<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[11px] font-bold text-primary-700">
											{job.initials}
										</span>
										<div className="min-w-0 flex-1">
											<p className="truncate text-[13px] font-semibold text-gray-900">{job.title}</p>
											<p className="truncate text-[11px] text-gray-500">{job.detail}</p>
										</div>
										<span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
											{job.salary}
										</span>
									</div>
								))}
							</div>
						</div>
						<div className="absolute -right-6 top-0 rotate-2 rounded-xl bg-white px-3.5 py-2.5 shadow-lg shadow-primary-950/40">
							<div className="flex items-center gap-2.5">
								<svg aria-hidden="true" className="size-8 -rotate-90" viewBox="0 0 28 28">
									<circle cx="14" cy="14" r="11" fill="none" className="stroke-primary-100" strokeWidth="3.5" />
									<circle
										cx="14"
										cy="14"
										r="11"
										fill="none"
										className="stroke-primary-600"
										strokeWidth="3.5"
										strokeLinecap="round"
										strokeDasharray={MATCH_RING_CIRCUMFERENCE}
										strokeDashoffset={MATCH_RING_CIRCUMFERENCE * (1 - 0.92)}
									/>
								</svg>
								<div>
									<p className="text-[13px] font-bold leading-tight text-primary-700">92% match</p>
									<p className="text-[10px] text-gray-500">Sales Manager</p>
								</div>
							</div>
						</div>
						<div className="absolute -left-6 bottom-0 -rotate-2 rounded-xl bg-white px-3.5 py-2.5 shadow-lg shadow-primary-950/40">
							<div className="flex items-center gap-2">
								<span className="flex size-5 items-center justify-center rounded-full bg-success-default/15 text-success-default">
									<CheckIcon aria-hidden="true" className="size-3.5" />
								</span>
								<div>
									<p className="text-[12px] font-semibold text-gray-900">Application sent</p>
									<p className="text-[10px] text-gray-500">Just now</p>
								</div>
							</div>
						</div>
					</div>

					<div className="relative mt-14 text-center">
						<h1 className="text-balance font-plus-jakarta text-3xl font-bold leading-[1.2] text-white">
							Your next job should find you.
						</h1>
						<p className="mx-auto mt-3 max-w-[42ch] text-[15px] leading-relaxed text-white/65">
							We track opportunities across Tanzania, from job boards to billboards and newspapers, so the right job
							never slips past you.
						</p>
					</div>
				</div>
			</div>
			<div className="bg-white md:h-screen md:overflow-y-auto">
				<div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center px-6 py-12 sm:px-8 xl:max-w-xl">
					{children}
				</div>
			</div>
		</section>
	);
}

export default AuthLayout;
